import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Shield, ChevronRight, Calendar, Building2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import PolicyForm from '../components/policy/PolicyForm';
import SearchInput from '../components/SearchInput';
import { policiesAPI } from '../services/api';
import { EMPTY_POLICY_FORM, formToPayload, getPolicyTypeMeta, getPolicyProgress } from '../config/policyFieldConfig';
import { formatCurrency, formatDate } from '../utils/formatters';
import { filterBySearch } from '../utils/search';

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_POLICY_FORM });
  const [search, setSearch] = useState('');

  const filteredPolicies = useMemo(
    () => filterBySearch(policies, search, [
      'company_name',
      'policy_number',
      'policy_type',
      'provider_name',
    ]),
    [policies, search]
  );

  const loadPolicies = () => {
    policiesAPI.list()
      .then(({ data }) => setPolicies(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPolicies();
    policiesAPI.providers().then(({ data }) => setProviders(data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await policiesAPI.create(formToPayload(form));
      setShowForm(false);
      setForm({ ...EMPTY_POLICY_FORM });
      loadPolicies();
      if (data?.id) window.location.href = `/policies/${data.id}`;
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create policy');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Company Policies</h1>
          <p className="text-slate-500 mt-1">Manage mediclaim and term life insurance policies</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" />
          Add Policy
        </button>
      </div>

      {showForm && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Create New Policy</h2>
          <PolicyForm
            form={form}
            onChange={setForm}
            providers={providers}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
            submitLabel="Create Policy"
          />
        </div>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search by company, policy number, type…"
        value={search}
        onChange={setSearch}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filteredPolicies.map((policy) => {
          const typeMeta = getPolicyTypeMeta(policy.policy_type);
          const progress = getPolicyProgress(policy.policy_start_date, policy.policy_end_date);

          return (
            <Link
              key={policy.id}
              to={`/policies/${policy.id}`}
              className="group block bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-primary-200 transition-all overflow-hidden"
            >
              <div className="h-1.5 bg-gradient-to-r from-primary-500 to-primary-700" />
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="p-2.5 rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
                    <Shield className="w-6 h-6" />
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeMeta.color}`}>
                    {typeMeta.label}
                  </span>
                </div>

                <h3 className="mt-4 font-bold text-slate-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                  {policy.company_name}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">{policy.provider_name}</p>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {policy.branch} · {policy.policy_number || 'N/A'}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(policy.policy_start_date)} — {formatDate(policy.policy_end_date)}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Sum Insured</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(policy.sum_insured)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{progress.daysLeft} days left</p>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-primary-500 ml-auto mt-1 transition-colors" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {policies.length === 0 && !showForm && (
        <div className="card text-center py-16 text-slate-500">
          <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          No policies yet. Click &quot;Add Policy&quot; to create your first one.
        </div>
      )}
      {policies.length > 0 && filteredPolicies.length === 0 && (
        <p className="text-center py-8 text-slate-500">No policies match your search.</p>
      )}
    </div>
  );
}
